const FILTERED = "[Filtered]";
const SENSITIVE_HEADER =
  /^(?:cookie|set-cookie|authorization|proxy-authorization|x-xsrf-token|x-csrf-token)$/i;
const SENSITIVE_KEY = /^(?:headers|cookies?|email)$/i;
const EMAIL = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SENSITIVE_PARAM =
  /([?&#](?:code|state|[a-z_]*token|[a-z_]*secret|password|signature|api_key)=)[^&#]*/gi;
const URL_BREADCRUMB_KEYS = ["from", "to", "url"];
const MAX_DEPTH = 10;

type ScrubbableEvent = {
  user?: { email?: string };
  request?: {
    url?: string;
    headers?: Record<string, string>;
    cookies?: unknown;
  };
  breadcrumbs?: { data?: Record<string, unknown> }[];
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
};

/**
 * Strips the user's email, cookies and HTTP headers from a Sentry event before
 * it leaves the browser, whichever capture attached them, e.g.
 * `extra.reason.config.headers` → `"[Filtered]"` and
 * `"invited jane@acme.io"` → `"invited [Filtered]"`. Page and breadcrumb
 * URLs keep their path and lose credential query values, e.g.
 * `/github-app-setup?code=abc&tab=1` → `/github-app-setup?code=[Filtered]&tab=1`.
 */
export function scrubSentryEvent<E extends ScrubbableEvent>(event: E): E {
  if (event.user) delete event.user.email;

  if (event.request) {
    delete event.request.cookies;
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    const headers = event.request.headers;
    if (headers) {
      for (const name of Object.keys(headers)) {
        if (SENSITIVE_HEADER.test(name)) delete headers[name];
        else if (/^referer$/i.test(name))
          headers[name] = scrubUrl(headers[name]);
      }
    }
  }

  for (const { data } of event.breadcrumbs ?? []) {
    if (!data) continue;
    for (const key of URL_BREADCRUMB_KEYS) {
      const value = data[key];
      if (typeof value === "string") data[key] = scrubUrl(value);
    }
  }

  if (event.extra) scrubEntries(event.extra);
  if (event.contexts) scrubEntries(event.contexts);

  return event;
}

function scrubUrl(url: string): string {
  return url.replace(SENSITIVE_PARAM, `$1${FILTERED}`).replace(EMAIL, FILTERED);
}

function scrubEntries(record: Record<string, unknown>): void {
  for (const key of Object.keys(record)) {
    record[key] = scrubEntry(key, record[key], 0);
  }
}

function scrubEntry(key: string, value: unknown, depth: number): unknown {
  if (SENSITIVE_KEY.test(key) || SENSITIVE_HEADER.test(key)) return FILTERED;

  return scrubValue(value, depth);
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") return value.replace(EMAIL, FILTERED);
  if (depth >= MAX_DEPTH || typeof value !== "object" || value === null) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      scrubEntry(key, entry, depth + 1),
    ]),
  );
}
